import http from '@/lib/request/svc/hr/http';
import getInstallationId from '@/lib/facades/get-installation-id';

export interface JobTitle {
  id: string;
  name: string;
}

interface RawJobTitle {
  id?: string;
  name?: string;
  archived?: boolean;
}

interface RawJobTitleListData {
  jobTitles?: RawJobTitle[];
  pagination?: { nextCursor?: string };
}

/**
 * svc-hr wraps the payload in `{ success, data, error }` and `http.js` carries
 * only a request interceptor, so nothing unwraps it. Reading `jobTitles` off
 * the response root silently yields an empty library forever.
 */
interface RawJobTitleListEnvelope {
  success?: boolean;
  data?: RawJobTitleListData;
  error?: { message?: string };
}

/**
 * A page svc-hr did not answer with a successful envelope. Callers catch
 * generically, so this only has to be an Error they can surface; the message
 * is the server's where the envelope carried one.
 */
export class JobTitleListError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JobTitleListError';
  }
}

/** Mirrors svc-hr's maximum list page size. */
const PAGE_SIZE = 100;

/**
 * Backstop (10,000 titles) so a cursor that never ends cannot spin forever.
 * Tripping it is a failure, not a short read: a partial library would report
 * titles past the cap as not found.
 */
const MAX_PAGES = 100;

const MOCK_FLAG_KEY = 'job-titles:mock';

const MOCK_LIBRARY: JobTitle[] = [
  'Assistant Venue Manager (2IC)',
  'Bar Attendant',
  'Barista',
  'Cook',
  'Head Chef',
  'Kitchen Hand',
  'Shift Supervisor',
  'Venue Manager',
  'Waitstaff'
].map((name, index) => ({ id: `mock-${index + 1}`, name }));

/**
 * Opt-IN, so the real request is the default path on every environment
 * including tests. Enable with localStorage['job-titles:mock'] = 'true' while
 * no deployed environment serves the endpoint.
 */
function isMockEnabled(): boolean {
  if (process.env.NODE_ENV === 'production') {
    return false;
  }

  try {
    return localStorage.getItem(MOCK_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * proto3-JSON omits empty arrays and false booleans, so entries are read by
 * shape rather than truthiness. Archived titles are filtered out: a title
 * cannot be archived while held, so one can never be a live assignment.
 */
export function toJobTitles(data: RawJobTitleListData | undefined): JobTitle[] {
  const raw = Array.isArray(data?.jobTitles) ? data.jobTitles : [];

  return raw
    .filter((entry): entry is RawJobTitle & { id: string } => typeof entry?.id === 'string')
    .filter(entry => entry.archived !== true)
    .map(entry => ({
      id: entry.id,
      name: typeof entry.name === 'string' ? entry.name : ''
    }));
}

/**
 * Walks every page. The select has no lazy loading, so a title sitting past the
 * first page would be unselectable and an existing assignment on a later page
 * would not resolve to a name.
 */
export async function listJobTitles(): Promise<JobTitle[]> {
  if (isMockEnabled()) {
    return MOCK_LIBRARY;
  }

  const tenantId = getInstallationId();
  const titles: JobTitle[] = [];
  let cursor: string | undefined;
  let pages = 0;

  do {
    const { data } = await http.get(`/tenants/${tenantId}/job-titles`, {
      params: cursor ? { limit: PAGE_SIZE, cursor } : { limit: PAGE_SIZE },
    });

    const page = readJobTitlePage(data);

    titles.push(...page.titles);

    cursor = page.nextCursor;
    pages += 1;
  } while (cursor && pages < MAX_PAGES);

  assertPagingComplete(cursor);

  return titles;
}

function assertPagingComplete(cursor: string | undefined): void {
  if (cursor) {
    throw new JobTitleListError(
      `job title list did not complete within ${MAX_PAGES} pages`
    );
  }
}

/**
 * A 200 carrying `success` false, or an envelope with no `data`, is a failed
 * read rather than an empty library: returning `[]` for either would render a
 * library that looks merely empty and skip the caller's load-failed handling.
 */
export function readJobTitlePage(response: unknown): {
  titles: JobTitle[];
  nextCursor: string | undefined;
} {
  const { payload, nextCursor } = readJobTitlePayload(response);

  return { titles: toJobTitles(payload), nextCursor };
}

function readJobTitlePayload(response: unknown): {
  payload: RawJobTitleListData;
  nextCursor: string | undefined;
} {
  const envelope = response as RawJobTitleListEnvelope | undefined;

  if (envelope?.success !== true) {
    throw new JobTitleListError(
      envelope?.error?.message || 'list job titles failed'
    );
  }

  const payload = envelope.data;

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new JobTitleListError('list job titles returned no data');
  }

  const next = payload.pagination?.nextCursor;

  return {
    payload,
    nextCursor: typeof next === 'string' && next !== '' ? next : undefined,
  };
}

/**
 * Names of the archived titles only, read with svc-hr's `ARCHIVED` status
 * filter because the default list omits them.
 */
export async function listArchivedJobTitleNames(): Promise<string[]> {
  if (isMockEnabled()) {
    return [];
  }

  const tenantId = getInstallationId();
  const names: string[] = [];
  let cursor: string | undefined;
  let pages = 0;

  do {
    const { data } = await http.get(`/tenants/${tenantId}/job-titles`, {
      params: {
        limit: PAGE_SIZE,
        status: 'ARCHIVED',
        ...(cursor ? { cursor } : {}),
      },
    });

    const page = readJobTitlePayload(data);
    const raw = Array.isArray(page.payload.jobTitles)
      ? page.payload.jobTitles
      : [];

    raw.forEach(entry => {
      if (
        entry?.archived === true &&
        typeof entry.name === 'string' &&
        entry.name.trim() !== ''
      ) {
        names.push(entry.name);
      }
    });

    cursor = page.nextCursor;
    pages += 1;
  } while (cursor && pages < MAX_PAGES);

  assertPagingComplete(cursor);

  return names;
}

export default listJobTitles;
